import React, { useState, useRef, useEffect } from "react";
import { API_BASE_URL } from "../../config";
import { useAuth } from "../../contexts/AuthContext";
import {
  MapPin,
  SkipForward,
  UserPlus,
  UserMinus,
  Download,
  FileText,
  Upload,
  CheckCircle,
  X,
  Loader2,
  RefreshCw,
} from "lucide-react";
import ClientSelector from "../ClientSelector";

function ManageSectors() {
  const [file, setFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [message, setMessage] = useState({ text: "", type: "" });
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadResult, setUploadResult] = useState(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState("");
  const fileInputRef = useRef(null);
  const { fetchWithAuth } = useAuth();

  const showMessage = (text, type) => {
    setMessage({ text, type });
    setTimeout(() => setMessage({ text: "", type: "" }), 10000);
  };

  const handleFileSelect = (selectedFile) => {
    if (
      selectedFile &&
      selectedFile.type ===
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    ) {
      setFile(selectedFile);
      setUploadResult(null); // Clear previous results
      showMessage("File selected successfully", "success");
    } else {
      showMessage("Please select a valid Excel file (.xlsx)", "error");
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFile = e.dataTransfer.files[0];
    handleFileSelect(droppedFile);
  };

  const handleFileInputChange = (e) => {
    const selectedFile = e.target.files[0];
    handleFileSelect(selectedFile);
  };

  const handleUpload = async () => {
    if (!file) {
      showMessage("Please select a file first", "error");
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);

    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await fetchWithAuth(`/sectors/sync`, {
        method: "POST",
        body: formData,
      });

      if (response.ok) {
        const result = await response.json();
        setUploadResult(result);

        // Show success message
        const successMessage = `Successfully processed sectors. ${
          result.intervention_point_mappings_created || 0
        } intervention point sector mappings created, ${
          result.intervention_point_mappings_skipped || 0
        } intervention point sector mappings skipped, ${
          result.user_mappings_created || 0
        } user sector mappings created, ${
          result.user_mappings_skipped || 0
        } user sector mappings skipped.`;

        if (result.total_errors > 0) {
          showMessage(
            `${successMessage} ${result.total_errors} errors occurred.`,
            "warning"
          );
        } else {
          showMessage(successMessage, "success");
        }

        setFile(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
      } else {
        const errorData = await response.json();
        showMessage(errorData.message || "Upload failed", "error");
      }
    } catch (error) {
      console.error("Upload error:", error);
      showMessage("Upload failed. Please try again.", "error");
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const downloadTemplate = async (includeData = false) => {
    setIsDownloading(true);
    try {
      const endpoint = includeData
        ? `/sectors/template-with-data?client_id=${selectedClient}`
        : `/sectors/template`;

      const response = await fetchWithAuth(endpoint);

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = includeData
          ? "sectors_with_existing_data.xlsx"
          : "sectors_template.xlsx";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        showMessage(
          includeData
            ? "Downloaded template with existing data"
            : "Downloaded empty template",
          "success"
        );
      } else {
        showMessage("Failed to download template", "error");
      }
    } catch (error) {
      console.error("Error downloading template:", error);
      showMessage("Failed to download template", "error");
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="bg-white dark:bg-gray-800 shadow-md rounded-lg p-6">
        <button
          onClick={() => window.history.back()}
          className="mr-4 mb-4 px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
        >
          ← Back
        </button>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-6">
          Manage Sectors
        </h1>

        <div className="mb-8">
          <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-200 mb-4">
            About Sectors Management
          </h2>
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
            <p className="text-gray-700 dark:text-gray-300 mb-3">
              Sectors help organize intervention points and users by grouping
              them together. Each sector is associated with a client and can
              have multiple users and intervention points assigned to it.
            </p>
            <p className="text-gray-700 dark:text-gray-300 mb-3">
              <strong>Process Overview:</strong>
            </p>
            <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 space-y-1 ml-4">
              <li>
                Download existing data to see current sector configurations
              </li>
              <li>Modify the Excel file to add/update sector assignments</li>
              <li>Upload the file to synchronize changes</li>
              <li>New sectors are created automatically if they don't exist</li>
              <li>Duplicate mappings are prevented automatically</li>
            </ul>
          </div>
        </div>

        <div className="mb-8">
          <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-200 mb-4">
            Template File Structure
          </h2>
          <div className="overflow-x-auto">
            <table className="min-w-full bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg">
              <thead className="bg-gray-50 dark:bg-gray-600">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Column Name
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Description
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Mandatory
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-600">
                <tr>
                  <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
                    client_id
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                    The client ID
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                    <span className="text-red-600 dark:text-red-400 font-medium">
                      Yes
                    </span>
                  </td>
                </tr>
                <tr>
                  <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
                    user_id
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                    The user ID
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                    No
                  </td>
                </tr>
                <tr>
                  <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
                    intervention_point_id
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                    The intervention point ID
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                    <span className="text-red-600 dark:text-red-400 font-medium">
                      Yes
                    </span>
                  </td>
                </tr>
                <tr>
                  <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
                    intervention_point_name
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                    The intervention point name
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                    No
                  </td>
                </tr>
                <tr>
                  <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
                    sector_id
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                    The sector ID if already existing
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                    No
                  </td>
                </tr>
                <tr>
                  <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
                    sector_name
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                    The sector name
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                    <span className="text-red-600 dark:text-red-400 font-medium">
                      Yes
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div className="mb-6 space-y-3">
          {/* Client Selection */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Select Client
            </label>
            <ClientSelector
              selectedClient={selectedClient}
              onClientChange={setSelectedClient}
              onError={showMessage}
              placeholder="Choose a client..."
            />
          </div>

          <button
            onClick={() => downloadTemplate(false)}
            disabled={isDownloading}
            className="inline-flex items-center px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white font-medium rounded-md transition-colors mr-3"
          >
            <Download className="w-5 h-5 mr-2" />
            {isDownloading ? "Downloading..." : "Download Empty Template"}
          </button>

          <button
            onClick={() => downloadTemplate(true)}
            disabled={isDownloading}
            className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white font-medium rounded-md transition-colors"
          >
            <FileText className="w-5 h-5 mr-2" />
            {isDownloading
              ? "Downloading..."
              : "Download Template with Existing Data"}
          </button>
        </div>

        <div className="mb-6">
          <div
            className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
              isDragging
                ? "border-blue-500 bg-blue-50 dark:bg-blue-900/20"
                : "border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500"
            }`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <div className="space-y-4">
              <Upload className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500" />
              <div>
                <p className="text-lg font-medium text-gray-900 dark:text-white">
                  {file ? file.name : "Upload Excel file"}
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {file
                    ? "File selected successfully"
                    : "Drag and drop an Excel file here, or click to browse"}
                </p>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileInputChange}
                className="hidden"
              />
              {!file && (
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-md transition-colors"
                >
                  Browse Files
                </button>
              )}
            </div>
          </div>
        </div>

        {file && (
          <div className="mb-6">
            <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <div className="flex items-center space-x-3">
                <CheckCircle className="w-8 h-8 text-green-500" />
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    {file.name}
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {(file.size / 1024 / 1024).toFixed(2)} MB
                  </p>
                </div>
              </div>
              <button
                onClick={() => {
                  setFile(null);
                  if (fileInputRef.current) {
                    fileInputRef.current.value = "";
                  }
                }}
                className="text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}

        {message.text && (
          <div
            className={`mb-6 p-4 rounded-lg ${
              message.type === "success"
                ? "bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-green-800 dark:text-green-200"
                : message.type === "warning"
                ? "bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 text-yellow-800 dark:text-yellow-200"
                : "bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-800 dark:text-red-200"
            }`}
          >
            {message.text}
          </div>
        )}

        {uploadResult && (
          <div className="mb-6">
            <div className="bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg overflow-hidden">
              <div className="px-4 py-3 bg-gray-50 dark:bg-gray-600 border-b border-gray-200 dark:border-gray-500">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Synchronization Results
                </h3>
              </div>

              <div className="p-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4 justify-items-center">
                  <div className="text-center">
                    <div className="flex items-center justify-center mb-2">
                      <MapPin className="w-6 h-6 text-green-600 dark:text-green-400 mr-2" />
                      <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                        {uploadResult.intervention_point_mappings_created || 0}
                      </div>
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      intervention point mappings created
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="flex items-center justify-center mb-2">
                      <SkipForward className="w-6 h-6 text-blue-600 dark:text-blue-400 mr-2" />
                      <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                        {uploadResult.intervention_point_mappings_skipped || 0}
                      </div>
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      intervention point mappings skipped
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="flex items-center justify-center mb-2">
                      <UserPlus className="w-6 h-6 text-purple-600 dark:text-purple-400 mr-2" />
                      <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                        {uploadResult.user_mappings_created || 0}
                      </div>
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      user mappings created
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="flex items-center justify-center mb-2">
                      <UserMinus className="w-6 h-6 text-blue-600 dark:text-blue-400 mr-2" />
                      <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                        {uploadResult.user_mappings_skipped || 0}
                      </div>
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      user mappings skipped
                    </div>
                  </div>
                </div>
                {uploadResult.errors && uploadResult.errors.length > 0 && (
                  <div className="mt-4">
                    <h4 className="text-md font-semibold text-gray-900 dark:text-white mb-2">
                      Error Details
                    </h4>
                    <div className="max-h-60 overflow-y-auto bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
                      <ul className="space-y-1">
                        {uploadResult.errors.map((error, index) => (
                          <li
                            key={index}
                            className="text-sm text-red-700 dark:text-red-300 font-mono"
                          >
                            {error}
                          </li>
                        ))}
                      </ul>
                      {uploadResult.total_errors >
                        uploadResult.errors.length && (
                        <div className="mt-2 text-sm text-red-600 dark:text-red-400 font-medium">
                          ... and{" "}
                          {uploadResult.total_errors -
                            uploadResult.errors.length}{" "}
                          more errors
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        <div className="flex justify-end">
          <button
            onClick={handleUpload}
            disabled={!file || isUploading}
            className={`inline-flex items-center px-6 py-3 font-medium rounded-md transition-colors ${
              !file || isUploading
                ? "bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed"
                : "bg-blue-600 hover:bg-blue-700 text-white"
            }`}
          >
            {isUploading ? (
              <>
                <Loader2 className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" />
                Processing...
              </>
            ) : (
              <>
                <RefreshCw className="w-5 h-5 mr-2" />
                Synchronize Sectors
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ManageSectors;
